/**
 * "FOLLOW THIS LEAD": the room writes a name and a sentence for the lens you
 * have on, you fix them if you like, and the room follows it from then on.
 * Opens under the lens bar; nothing is saved until Follow.
 */
import { useEffect, useState, type ReactNode } from 'react';

export default function FollowLead({
  label,
  inside,
  draft,
  onFollow,
  onCancel,
}: {
  /** The lens, drawn as a sentence (`LensPhrase`). */
  label: ReactNode;
  /** The lead this one would sit inside, if the room is following one. */
  inside: string | null;
  draft: () => Promise<{ title: string; text: string }>;
  onFollow: (body: { title: string; text: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [drafting, setDrafting] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    draft()
      .then(d => {
        if (!live) return;
        setTitle(t => t || d.title);
        setText(t => t || d.text);
      })
      .catch(() => {
        /* the person writes it themselves */
      })
      .finally(() => live && setDrafting(false));
    return () => {
      live = false;
    };
    // Drafted once, for the lens the form was opened on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const follow = async () => {
    setSaving(true);
    try {
      await onFollow({ title: title.trim(), text: text.trim() });
    } catch {
      /* the page shows the error; the form stays open */
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="shrink-0 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm shadow-sm dark:border-indigo-800 dark:bg-gray-800"
      data-testid="follow-lead"
    >
      <div className="flex flex-wrap items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
        <span>Follow</span>
        {label}
        {inside && (
          <span>
            inside <span className="font-medium">{inside}</span>
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Digs will stay inside it, and everything they find still goes on the board. You can go back at any time.
      </p>
      <div className="mt-2 space-y-1.5">
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={drafting ? 'Writing a name…' : 'A short name'}
          aria-label="Lead name"
          maxLength={120}
          className="w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
        />
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={drafting ? 'Writing what you are looking for…' : 'What are you looking for here?'}
          aria-label="What you are looking for"
          rows={2}
          maxLength={1000}
          className="w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
        />
      </div>
      <div className="mt-2 flex gap-1.5">
        <button
          type="button"
          onClick={() => void follow()}
          disabled={saving || !title.trim() || !text.trim()}
          className="rounded bg-indigo-600 px-2.5 py-1 text-xs text-white hover:bg-indigo-700 disabled:opacity-50"
          data-testid="follow-lead-go"
        >
          Follow
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
